import { FlexboxProps, TextProps, Theme, ValueOf } from '@redsift/design-system';
import { PopoverProps } from '@redsift/popovers';
import { ReactElement, RefObject } from 'react';

/**
 * Component variant.
 */
export const ComboboxSelectionMode = {
  single: 'single',
  multiple: 'multiple',
} as const;
export type ComboboxSelectionMode = ValueOf<typeof ComboboxSelectionMode>;

export const ComboboxVariant = {
  options: 'options',
  suggestions: 'suggestions',
} as const;
export type ComboboxVariant = ValueOf<typeof ComboboxVariant>;

export type ComboboxValue = string | string[];

/**
 * Context props.
 */
export type ComboboxState = {
  /** Filtering parameters. */
  readonly filter?: {
    type: 'startsWith' | 'contains' | 'endsWith';
    caseSensitive?: boolean;
  };
  /** Whether the combobox is disabled or not. */
  readonly isDisabled: boolean;
  /** Whether the combobox is invalid or not. */
  readonly isInvalid: boolean;
  /** Sets the selected value. */
  setValue(value: ComboboxValue): void;
  /** Current selected value. */
  readonly value: ComboboxValue;
  /** Sets the input value. */
  setInputValue(inputValue: string): void;
  /** Current input value. */
  readonly inputValue: string;
  /** Whether the Combobox allow multiple selection or not. */
  selectionMode?: ComboboxSelectionMode;
  /** If 'options', the input field cannot have free text, the selection has to be made within the options. If 'suggestions', then choosing from the list isn't mandatory and the user can write free text within the input. */
  variant?: ComboboxVariant;
  /** Ref to the first item used to create values. */
  freeTextItemRef?: RefObject<HTMLDivElement>;
  /** Id of the first item used to create values. */
  freeTextItemId?: string;
  /** Class name to append to the trigger. */
  readonly triggerClassName?: string;
  /** Ref to the form, if any. */
  formRef?: RefObject<HTMLFormElement>;
  /** Ref to the submit button, if any. */
  submitRef?: RefObject<HTMLButtonElement>;
};

/**
 * Component props.
 */
export interface ComboboxProps extends PopoverProps {
  /**
   * If 'options', the input field cannot have free text, the selection has to be made within the options. If 'suggestions', then choosing from the list isn't mandatory and the user can write free text within the input.
   * @default 'options'
   */
  variant?: ComboboxVariant;
  /**
   * Default selected value.
   * Used for uncontrolled version.
   */
  defaultValue?: ComboboxValue;
  /** Description of the combobox. */
  description?: string | ReactElement;
  /** Additional description properties. */
  descriptionProps?: Omit<TextProps, 'ref'>;
  /** Filtering parameters. */
  filter?: {
    type: 'startsWith' | 'contains' | 'endsWith';
    caseSensitive?: boolean;
  };
  /** Ref to the form, if any. */
  formRef?: RefObject<HTMLFormElement>;
  /** Ref to the submit button, if any. */
  submitRef?: RefObject<HTMLButtonElement>;
  /** Whether the component is disabled or not. */
  isDisabled?: boolean;
  /** Whether the component is invalid or not. */
  isInvalid?: boolean;
  /** Maximum number of options displayed. Useful when too many options are displayed. */
  maxOptionsLength?: number;
  /** Method to handle selection change. */
  onChange?(value: ComboboxValue): void;
  /** Method to handle input change. */
  onInputChange?(value: string): void;
  /**
   * Currently selected value.
   * Used for controlled version.
   */
  value?: ComboboxValue;
  /** Input value. */
  inputValue?: string;
  /** Whether the Combobox allow multiple selection or not. */
  selectionMode?: ComboboxSelectionMode;
  /** Theme. */
  theme?: Theme;
  /** Class name to append to the trigger. */
  triggerClassName?: string;
  /** Props to forward to the wrapper. */
  wrapperProps?: Omit<FlexboxProps, 'ref'>;
}

export type StyledComboboxProps = ComboboxProps;
